import i18n from "i18next";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { booleanColumn, dateTimeColumn, moneyColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { CustomerOrderChangeView } from "src/api/generated/erp/db/types/tables/customerOrderChangeView.ts";
import { CustomerOrderChangeApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderChangeApi.ts";
import { CustomerOrderChangeForm } from "./CustomerOrderChangeForm.tsx";
import { CustomerOrderChangeObjectType } from "src/api/generated/erp/db/types/enums/customerOrderChangeObjectType.ts";
import { OpenObjectChangeLogButton } from "../../../changeLogging/OpenObjectChangeLogButton.tsx";
import { renderMoneyCellWithRowCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderChangesDataGridProps {
	objectType: CustomerOrderChangeObjectType;
	objectId: number;
	refreshRef?: RefreshableElementRef;
	readOnly?: boolean;
}

export const CustomerOrderChangesDataGrid = ({
	objectType,
	objectId,
	refreshRef,
	readOnly = false,
}: CustomerOrderChangesDataGridProps) => {
	// Offer order changes are created by reconfiguring offer lines, so only their subject can be edited.
	const offerChanges = objectType === "CUSTOMER_OFFER";
	return (
		<ClientSideDataGridModel
			customFunctionUiActionView={"CUSTOMER_ORDER_CHANGES"}
			fetchData={() =>
				offerChanges ?
					CustomerOrderChangeApi.getCustomerOfferOrderChanges({ customerOrderOfferId: objectId })
				:	CustomerOrderChangeApi.getCustomerOrderChanges({ objectType, objectId })
			}
			getRows={(data) => data}
			getRowId={(row) => row.customerOrderChangeId}
			gridId={"D36DD108AF0C40E4"}
			initialParams={{}}
			refreshRef={refreshRef}
			render={({ dataGridProps }) => (
				<FormCrudDataGrid<CustomerOrderChangeView>
					columns={[
						textColumn({ field: "subject", headerName: i18n.t("subject"), width: 250 }),
						moneyColumn({
							field: "costChange",
							headerName: i18n.t("cost_change"),
							width: 120,
							renderCell: renderMoneyCellWithRowCurrency((row) => row.costCurrencyCode),
						}),
						moneyColumn({
							field: "priceChange",
							headerName: i18n.t("price_change"),
							width: 120,
							renderCell: renderMoneyCellWithRowCurrency((row) => row.priceCurrencyCode),
						}),
						booleanColumn({
							field: "printOnOrderConfirmation",
							headerName: i18n.t("print_on_order_confirmation"),
							width: 160,
						}),
						textColumn({
							field: "createdByUserName",
							headerName: i18n.t("created_by"),
							width: 140,
						}),
						dateTimeColumn({ field: "createdAt", headerName: i18n.t("created_at"), width: 160 }),
					]}
					form={{
						addRowEnabled: !readOnly && !offerChanges,
						editEnabled: !readOnly,
						dialogSize: "md",
						dialogTitle: i18n.t("order_change"),
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.sales.customerOrderChange.formDialog"),
							data: {
								objectType,
								objectId,
								customerOrderChangeId: row?.customerOrderChangeId ?? null,
							},
							pathSegments:
								row?.customerOrderChangeId ?
									[`${i18n.t("order_change")} ${row.customerOrderChangeId}`]
								:	[i18n.t("order_change")],
						}),
						component: ({ row, onCompleted, onFormEdited }) => (
							<CustomerOrderChangeForm
								objectType={objectType}
								objectId={objectId}
								existingCustomerOrderChange={row}
								subjectOnly={offerChanges}
								onCompleted={onCompleted}
								onFormEdited={onFormEdited}
							/>
						),
					}}
					remove={
						!readOnly &&
						!offerChanges && {
							type: "enabled",
							action: async ({ row }) => {
								await CustomerOrderChangeApi.delete_({
									customerOrderChangeId: row.customerOrderChangeId,
								});
							},
						}
					}
					rowContextMenuComponents={({ row }) => [
						<OpenObjectChangeLogButton
							key={"changeLog"}
							objectRef={{
								objectType: "CUSTOMER_ORDER_CHANGE",
								objectId: row.customerOrderChangeId,
							}}
						/>,
					]}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.customerOrderChanges.grid"),
						data: { objectType, objectId },
						selectedRowPathSegment: (row) => `${i18n.t("order_change")} ${row.customerOrderChangeId}`,
						rowFields: ["customerOrderChangeId", "objectType", "objectId", "subject"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
